import { Injectable, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

import { PermissionService } from '../../services/permission.service';
import { navTabsFor } from '../navbar/nav-tabs';

/** One crumb in the trail. */
export interface BreadcrumbItem {
  readonly label: string;
  /** Absent on the last crumb, which is the page already shown. */
  readonly routerLink?: string;
  readonly queryParams?: Record<string, string>;
}

/**
 * A route's place in the trail, declared in app.routes.ts as
 * `data: { breadcrumb: { ... } }`.
 *
 * A route with no `breadcrumb` entry shows no breadcrumb at all. That is the
 * right answer for every page the navbar links to directly: "Home & Search >
 * Reports" only repeats the tab already highlighted above it.
 */
export interface RouteBreadcrumb {
  /**
   * What the last crumb says. On a page about one particular record this is
   * only the placeholder shown until the record arrives — the page replaces it
   * via `setRecordLabel`.
   */
  readonly label: string;
  /**
   * The route of the navbar tab this page sits under, e.g. '/pac-feed'. The
   * crumb takes its wording from that tab. Omit for pages that hang directly
   * off the user's home.
   */
  readonly parent?: string;
}

/** Shortest search term the basic officer search will remember. */
const MIN_REMEMBERED_SEARCH_LENGTH = 3;

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);

  /**
   * The name of the record the current page is showing, once it has loaded.
   * Cleared on every NavigationStart — which fires before the next page's
   * component is created — so a name can never be left behind on the page
   * after it.
   */
  private readonly recordLabel = signal<string | null>(null);

  /** The `data.breadcrumb` of the deepest activated route, or null. */
  private readonly routeBreadcrumb = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.deepestBreadcrumb()),
      startWith(this.deepestBreadcrumb()),
    ),
    { initialValue: null as RouteBreadcrumb | null },
  );

  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => this.recordLabel.set(null));
  }

  /**
   * Called by a page once it knows what record it is showing, so the last
   * crumb can name it. Pass null to fall back to the route's own label.
   */
  setRecordLabel(label: string | null): void {
    this.recordLabel.set(label?.trim() || null);
  }

  readonly items = computed<BreadcrumbItem[]>(() => {
    const crumb = this.routeBreadcrumb();
    if (!crumb) return [];

    const tabs = navTabsFor(
      this.permissionService.selectedPermission(),
      this.permissionService.selectedAcademicOfficer(),
    );
    const home = tabs[0];
    if (!home) return [];

    const trail: BreadcrumbItem[] = [
      { label: home.label, routerLink: home.route, queryParams: this.homeQueryParams(home.route) },
    ];

    // A parent the user has no tab for is left out rather than shown as dead
    // text: an Academic Officer raising leave has no admin worklist to go back
    // to, so offering them one would be a route that does not exist.
    const parent = crumb.parent ? tabs.find(t => t.route === crumb.parent) : undefined;
    if (parent) {
      trail.push({ label: parent.label, routerLink: parent.route });
    }

    trail.push({ label: this.recordLabel() ?? crumb.label });
    return trail;
  });

  /**
   * The basic officer search the user came from, carried on the crumb that
   * leads back to it so the retained results are matched rather than
   * discarded — see features/basic-search-retention.feature, which owns that
   * behaviour. Any other destination gets no query.
   */
  private homeQueryParams(route: string): Record<string, string> | undefined {
    if (route !== '/dashboard') return undefined;
    const remembered = sessionStorage.getItem('lastOfficerSearch')?.trim();
    return remembered && remembered.length >= MIN_REMEMBERED_SEARCH_LENGTH
      ? { q: remembered }
      : undefined;
  }

  private deepestBreadcrumb(): RouteBreadcrumb | null {
    let route = this.route.snapshot;
    while (route.firstChild) route = route.firstChild;
    return (route.data?.['breadcrumb'] as RouteBreadcrumb) ?? null;
  }
}
